// Poses for the scene: the value of every pose driver at a crank angle.
//
// The scene asks a PoseSource for the drivers at the angle it draws. Until the
// main thread can call the wasm module's pose_at (#28, #33), the source is the
// module's pose table, data/pose/<module>.json: pose_at at every whole degree
// of the 720° cycle (docs/contracts/gltf-parts.md § Pose tables). Between the
// degrees it interpolates with a periodic Catmull-Rom spline, which matches
// pose_at to about 1e-8 m on the fixture (web/tests/scene/pose.spec.ts) and is
// exact at whole degrees.

/** data/pose/<module>.json (schemas/pose-table.schema.json). */
export interface PoseTable {
  module: string;
  generated_by: string;
  drivers: string[];
  slow_state: number[];
  steps: number;
  /** One row per step: the crank angle in radians, then one value per driver. */
  samples: number[][];
}

/** The drivers at a crank angle. */
export interface PoseSource {
  readonly drivers: readonly string[];
  /** Writes each driver's value at crank angle `theta` (radians, any value; the cycle is 4π) into `out`. */
  sample(theta: number, out: Float64Array): void;
}

export const CYCLE = 4 * Math.PI;
const TWO_PI = 2 * Math.PI;

/** theta in [0, 4π). */
export function wrapCycle(theta: number): number {
  const t = theta % CYCLE;
  return t < 0 ? t + CYCLE : t;
}

/**
 * A pose source from a pose table. Angle drivers wrap at 2π within the table;
 * they are unwrapped first, so the spline never sees a jump. A driver that
 * never wraps is held within the range of its rows, so the spline cannot
 * overshoot where a driver has a kink: a valve lift that leaves 0 stays at
 * or above 0 between rows.
 */
export function tablePose(table: PoseTable): PoseSource {
  const n = table.samples.length;
  if (n < 4 || n !== table.steps) throw new Error(`pose table ${table.module}: ${n} rows for ${table.steps} steps`);
  const count = table.drivers.length;
  // values[d * n + k]: driver d at step k, unwrapped; lap[d]: its change over one cycle.
  const values = new Float64Array(count * n);
  const lap = new Float64Array(count);
  // low[d] and high[d]: the range of a driver that never wraps (±Infinity for one that does).
  const low = new Float64Array(count);
  const high = new Float64Array(count);
  for (let d = 0; d < count; d++) {
    let offset = 0;
    let previous = Number.NaN;
    let wraps = false;
    low[d] = Infinity;
    high[d] = -Infinity;
    for (let k = 0; k < n; k++) {
      const raw = table.samples[k]![d + 1]!;
      if (k > 0) offset += TWO_PI * Math.round((previous - raw) / TWO_PI);
      if (offset !== 0) wraps = true;
      previous = raw;
      values[d * n + k] = raw + offset;
      low[d] = Math.min(low[d]!, raw);
      high[d] = Math.max(high[d]!, raw);
    }
    // The step from the last row to the first row of the next cycle.
    const last = values[d * n + n - 1]!;
    const slope = last - values[d * n + n - 2]!;
    const first = values[d * n]!;
    lap[d] = TWO_PI * Math.round((last + slope - first) / TWO_PI);
    if (wraps || lap[d] !== 0) {
      low[d] = -Infinity;
      high[d] = Infinity;
    }
  }
  const step = CYCLE / n;
  const at = (d: number, k: number): number => {
    const wraps = Math.floor(k / n);
    return values[d * n + (k - wraps * n)]! + wraps * lap[d]!;
  };
  return {
    drivers: table.drivers,
    sample(theta, out) {
      const x = wrapCycle(theta) / step;
      const k = Math.floor(x);
      const t = x - k;
      const t2 = t * t;
      const t3 = t2 * t;
      // Catmull-Rom weights of the points k-1, k, k+1, k+2.
      const w0 = (-t3 + 2 * t2 - t) / 2;
      const w1 = (3 * t3 - 5 * t2 + 2) / 2;
      const w2 = (-3 * t3 + 4 * t2 + t) / 2;
      const w3 = (t3 - t2) / 2;
      for (let d = 0; d < count; d++) {
        const v = t === 0 ? at(d, k) : w0 * at(d, k - 1) + w1 * at(d, k) + w2 * at(d, k + 1) + w3 * at(d, k + 2);
        out[d] = Math.min(high[d]!, Math.max(low[d]!, v));
      }
    },
  };
}
